// Redux
import { Dispatch } from 'redux';

// Classes
import { DemographicsReducerState } from '../Demographics/classes/demographics.classes';
import { EngagementReducerState } from '../Engagement/classes/engagement.classes';
import { ProspectProfileReducerState } from '../ProspectProfile/classes/prospectProfile.classes';
import { ListMembershipReducerState } from '../ListMembership/classes/listMembership.classes';
import { LocationsReducerState } from '../Locations/classes/locations.classes';

// Action Creators
import {
  setProspectiveStudentsQuery,
  setParentsOfProspectsQuery,
  setOtherQuery,
  setUSAQuery,
  setInternationalQuery,
  setAgeQuery,
  setGenderQuery,
  setEthnicityQuery,
  setIntendedLevelOfStudyQuery,
  setEntryTermQuery,
  setIntendedMajorQuery,
  setGPAQuery,
  setTestScoresQuery,
  setDateRegisteredQuery,
  setRelativeEngagementQuery,
  setTotalTimeSpentQuery,
  setScheduledVisitQuery,
  setAppliedQuery,
  setActivityWithInstitutionQuery,
  setActivityWithEABQuery,
  setListMembershipQuery,
  setLocationsQuery,
  setQueryIsIncongruent,
} from '../actionCreators/query.actionCreators';

// Utilities
import {
  constructFilterObject,
  stringToDaysLookup,
  calendarDateTranslate,
} from '../utilities/query.utilities';
import { findDateFromDays } from '../../../../utils/findDateFromDays';

// Constants
import { MAPPING_OF_US_REGIONS_TO_US_STATES_AND_TERRITORIES_AND_THEIR_ABBREVIATIONS } from '../Demographics/Geography/constants/geography.constants';

/**
 * @function setProspectiveStudentsQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setProspectiveStudentsQueryValue(dispatch, demographics)
 */
export const setProspectiveStudentsQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const hsLevelQuery = [
    (demographics.visitorType.prospectiveStudents.hsFreshman &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Freshman')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.hsSophomore &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Sophomore')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.hsJunior &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Junior')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.hsSenior &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Senior')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  const filterObjectArray = [
    (hsLevelQuery.length > 0 && hsLevelQuery) || undefined,
    (demographics.visitorType.prospectiveStudents.hsGraduate &&
      constructFilterObject('visitorType', 'eq', 'hs_grad')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.collegeStudent &&
      constructFilterObject('visitorType', 'eq', 'college_student')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.collegeGraduate &&
      constructFilterObject('visitorType', 'eq', 'college_grad')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.adultLearner &&
      constructFilterObject('visitorType', 'eq', 'adult_learner')) ||
      undefined,
    (demographics.visitorType.prospectiveStudents.middleSchooler &&
      constructFilterObject('visitorType', 'eq', 'middle_school')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setProspectiveStudentsQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setParentsOfProspectsQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setParentsOfProspectsQueryValue(dispatch, demographics)
 */
export const setParentsOfProspectsQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const hsLevelQuery = [
    (demographics.visitorType.parentsOfProspects.hsFreshman &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Freshman')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.hsSophomore &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Sophomore')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.hsJunior &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Junior')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.hsSenior &&
      constructFilterObject('highSchoolLevelToday', 'eq', 'Senior')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  const filterObjectArray = [
    (hsLevelQuery.length > 0 && hsLevelQuery) || undefined,
    (demographics.visitorType.parentsOfProspects.hsGraduate &&
      constructFilterObject('visitorType', 'eq', 'parent_hs_grad')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.collegeStudent &&
      constructFilterObject('visitorType', 'eq', 'parent_college_student')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.collegeGraduate &&
      constructFilterObject('visitorType', 'eq', 'parent_college_grad')) ||
      undefined,
    (demographics.visitorType.parentsOfProspects.middleSchooler &&
      constructFilterObject('visitorType', 'eq', 'parent_middle_school')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setParentsOfProspectsQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setOtherQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setOtherQueryValue(dispatch, demographics)
 */
export const setOtherQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const filterObjectArray = [
    (demographics.visitorType.alumni && 'ALUMNI') || undefined,
    (demographics.visitorType.currentStudent &&
      constructFilterObject('visitorType', 'eq', 'current_student')) ||
      undefined,
    (demographics.visitorType.alumni &&
      constructFilterObject('visitorType', 'eq', 'alumni')) ||
      undefined,
    (demographics.visitorType.faculty &&
      constructFilterObject('visitorType', 'eq', 'faculty')) ||
      undefined,
    (demographics.visitorType.guidanceCounselor &&
      constructFilterObject('visitorType', 'eq', 'counselor')) ||
      undefined,
    (demographics.visitorType.veteran &&
      constructFilterObject('visitorType', 'eq', 'veteran')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setOtherQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setUSAQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @param {any[]} institutionStates - Information pertaining to where the institution
 * is located gotten from GQL.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setUSAQueryValue(dispatch, demographics)
 */
export const setUSAQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState,
  institutionStates: any[]
) => {
  const filterObjectArray: any[] = [];

  switch (demographics.geography.usa.selection) {
    case 'None':
      filterObjectArray.push('None');
      filterObjectArray.push(constructFilterObject('country', 'ne', 'USA'));
      break;
    case 'US Regions':
      if (demographics.geography.usa.value.length > 0) {
        filterObjectArray.push('US Regions');
        demographics.geography.usa.value.forEach((region: any) => {
          MAPPING_OF_US_REGIONS_TO_US_STATES_AND_TERRITORIES_AND_THEIR_ABBREVIATIONS[
            region
          ].forEach((state: any) => {
            filterObjectArray.push(
              constructFilterObject(
                'state',
                'eq',
                state[Object.keys(state)[0]].toUpperCase()
              )
            );
          });
        });
      } else {
        filterObjectArray.push('Any');
      }
      break;
    case 'In-State':
      filterObjectArray.push('In-State');
      institutionStates.forEach((state: any) => {
        filterObjectArray.push(
          constructFilterObject('state', 'eq', state.state_code.toUpperCase())
        );
      });
      break;
    case 'Out-of-State':
      filterObjectArray.push('Out-of-State');
      institutionStates.forEach((state: any) => {
        filterObjectArray.push(
          constructFilterObject('state', 'ne', state.state_code.toUpperCase())
        );
      });
      break;
    case 'Specific States/Territories':
      if (demographics.geography.usa.value.length > 0) {
        filterObjectArray.push('Specific States/Territories');
        demographics.geography.usa.value.forEach((state: any) => {
          filterObjectArray.push(
            constructFilterObject('state', 'eq', state.value.toUpperCase())
          );
        });
      } else {
        filterObjectArray.push('Any');
      }
      break;
    default:
      break;
  }
  dispatch(
    setUSAQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setInternationalQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setInternationalQueryValue(dispatch, demographics)
 */
export const setInternationalQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (demographics.geography.international.selection) {
    case 'None':
      filterObjectArray.push('None');
      filterObjectArray.push(constructFilterObject('country', 'eq', 'USA'));
      break;
    case 'Specific Countries':
      if (demographics.geography.international.value.length > 0) {
        filterObjectArray.push('Specific Countries');
        demographics.geography.international.value.forEach((country: any) => {
          filterObjectArray.push(
            constructFilterObject('country', 'eq', country.value)
          );
        });
      } else {
        filterObjectArray.push('Any');
      }
      break;
    default:
      break;
  }

  dispatch(
    setInternationalQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setAgeQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setAgeQueryValue(dispatch, demographics)
 */
export const setAgeQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const filterObjectArray: { column: string; op: string; value: string }[] = [];

  if (demographics.age.selection !== 'Any') {
    filterObjectArray.push(
      constructFilterObject('age', 'ge', demographics.age.value.lower)
    );
    if (demographics.age.value.upper !== 40) {
      filterObjectArray.push(
        constructFilterObject('age', 'le', demographics.age.value.upper)
      );
    }
  }

  dispatch(
    setAgeQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setGenderQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setGenderQueryValue(dispatch, demographics)
 */
export const setGenderQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const filterObjectArray = [
    (demographics.gender.male &&
      constructFilterObject('gender', 'eq', 'male')) ||
      undefined,
    (demographics.gender.female &&
      constructFilterObject('gender', 'eq', 'female')) ||
      undefined,
    (demographics.gender.otherThirdGender &&
      constructFilterObject('gender', 'ne', 'male')) ||
      undefined,
    (demographics.gender.otherThirdGender &&
      constructFilterObject('gender', 'ne', 'female')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setGenderQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setEthnicityQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {DemographicsReducerState} demographics - The current state
 * of the Demographics filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setEthnicityQueryValue(dispatch, demographics)
 */
export const setEthnicityQueryValue = (
  dispatch: Dispatch<any>,
  demographics: DemographicsReducerState
) => {
  const filterObjectArray = [
    (demographics.ethnicity.americanIndianOrAlaskaNative &&
      constructFilterObject(
        'ethnicity',
        'eq',
        'American Indian or Alaska Native'
      )) ||
      undefined,
    (demographics.ethnicity.asian &&
      constructFilterObject('ethnicity', 'eq', 'Asian')) ||
      undefined,
    (demographics.ethnicity.blackOrAfricanAmerican &&
      constructFilterObject('ethnicity', 'eq', 'Black or African American')) ||
      undefined,
    (demographics.ethnicity.hispanicOrLatin &&
      constructFilterObject('ethnicity', 'eq', 'Hispanic or Latin')) ||
      undefined,
    (demographics.ethnicity.mixedRace &&
      constructFilterObject('ethnicity', 'eq', 'Mixed Race')) ||
      undefined,
    (demographics.ethnicity.nativeHawaiianOrOtherPacificIslander &&
      constructFilterObject(
        'ethnicity',
        'eq',
        'Native Hawaiian or Other Pacific Islande'
      )) ||
      undefined,
    (demographics.ethnicity.other &&
      constructFilterObject('ethnicity', 'eq', 'Other')) ||
      undefined,
    (demographics.ethnicity.white &&
      constructFilterObject('ethnicity', 'eq', 'White')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setEthnicityQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setIntendedLevelofStudyQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ProspectProfileReducerState} prospectProfile - The current state
 * of the Prospect Profile filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setIntendedLevelofStudyQueryValue(dispatch, prospectProfile)
 */
export const setIntendedLevelofStudyQueryValue = (
  dispatch: Dispatch<any>,
  prospectProfile: ProspectProfileReducerState
) => {
  let filterObject = {};

  if (prospectProfile.intendedLevelOfStudy !== 'Any') {
    filterObject = constructFilterObject(
      'levelOfStudy',
      'eq',
      prospectProfile.intendedLevelOfStudy.toLowerCase()
    );
  }
  dispatch(
    setIntendedLevelOfStudyQuery(
      (Object.keys(filterObject).length > 0 && [filterObject]) || ''
    )
  );
};

/**
 * @function setEntryTermQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ProspectProfileReducerState} prospectProfile - The current state
 * of the Prospect Profile filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setEntryTermQueryValue(dispatch, prospectProfile)
 */
export const setEntryTermQueryValue = (
  dispatch: Dispatch<any>,
  prospectProfile: ProspectProfileReducerState
) => {
  let filterObjectArray: any[] = [];
  let values: { value: string }[];

  switch (prospectProfile.entryTerm.selection) {
    case 'Any':
      dispatch(setEntryTermQuery(''));
      break;
    case 'Term-Year':
      filterObjectArray.push('Term-Year');
      values = prospectProfile.entryTerm.value;
      filterObjectArray.push(
        ...values.map((value: { value: string }) => {
          const termYear = value.value.split(' ');
          const term =
            termYear[0] !== 'Undecided'
              ? termYear[0].toLowerCase()
              : 'Undecided';
          const year = termYear[1] || '-1';
          return [
            constructFilterObject(
              'enrollTerm',
              term === 'Undecided' ? 'like' : 'eq',
              `${term}`
            ),
            constructFilterObject('enrollYear', 'eq', `${year}`),
          ];
        })
      );
      dispatch(
        setEntryTermQuery(
          (filterObjectArray.length > 0 && filterObjectArray) || ''
        )
      );
      break;
    case 'Term':
      filterObjectArray = [
        'Term',
        (prospectProfile.entryTerm.value.fall &&
          constructFilterObject('enrollTerm', 'eq', 'fall')) ||
          undefined,
        (prospectProfile.entryTerm.value.spring &&
          constructFilterObject('enrollTerm', 'eq', 'spring')) ||
          undefined,
      ];
      dispatch(
        setEntryTermQuery(
          (filterObjectArray.length > 0 && filterObjectArray) || ''
        )
      );
      break;
    case 'Year':
      filterObjectArray = [
        'Year',
        constructFilterObject(
          'enrollYear',
          'ge',
          `${prospectProfile.entryTerm.value.lower}`
        ),
        constructFilterObject(
          'enrollYear',
          'le',
          `${prospectProfile.entryTerm.value.upper}`
        ),
      ];
      dispatch(
        setEntryTermQuery(
          (filterObjectArray.length > 0 && filterObjectArray) || ''
        )
      );
      break;
    default:
      break;
  }
};

/**
 * @function setIntendedMajorQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ProspectProfileReducerState} prospectProfile - The current state
 * of the Prospect Profile filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setIntendedMajorQueryValue(dispatch, prospectProfile)
 */
export const setIntendedMajorQueryValue = (
  dispatch: Dispatch<any>,
  prospectProfile: ProspectProfileReducerState,
  majors: any
) => {
  const filterObjectArray: any[] = [];
  const { intendedMajor } = prospectProfile;

  // 1 Did the user select Other Option as a Major
  const selectedOtherMajor = intendedMajor.some(
    (major: any) => major.value.toLocaleLowerCase() === 'other major'
  );

  if (selectedOtherMajor) {
    // Loop through the array.  We want to remove any of the majors that the user selected, from the intendedMajor array.
    const result = majors.reduce((acc: any, major: { value: string }) => {
      // Has the major been selected by the user?
      if (
        !intendedMajor.some(
          (valMajor: any) =>
            major.value.toLocaleLowerCase() ===
            valMajor.value.toLocaleLowerCase()
        )
      ) {
        const { value } = major;

        // Escape it and wrap in double quotes
        // if (value.includes(",")) {
        //   value = `"${value.replace(",", "\\,")}"`;
        // }
        acc.push(value);
      }
      return acc;
    }, []);

    // Create the Obj and push to the filterObjectArray
    // filterObjectArray.push(
    //   constructFilterObject(
    //     "major",
    //     "nin",
    //     result.length ? `'${result.join(",").toLocaleLowerCase()}` : ""
    //   )
    // );
    filterObjectArray.push(
      constructFilterObject(
        'major',
        'nin',
        result.length ? JSON.stringify(result) : ''
      )
    );
  } else
    intendedMajor.forEach((major: any) => {
      filterObjectArray.push(
        constructFilterObject('major', 'eq', major.value.toLowerCase())
      );
    });

  dispatch(
    setIntendedMajorQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setGPAQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ProspectProfileReducerState} prospectProfile - The current state
 * of the Prospect Profile filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setGPAQueryValue(dispatch, prospectProfile)
 */
export const setGPAQueryValue = (
  dispatch: Dispatch<any>,
  prospectProfile: ProspectProfileReducerState
) => {
  const filterObjectArray: { column: string; op: string; value: string }[] = [];

  if (prospectProfile.gpa.selection !== 'Any') {
    filterObjectArray.push(
      constructFilterObject('gpa', 'ge', prospectProfile.gpa.value.lower)
    );
    filterObjectArray.push(
      constructFilterObject('gpa', 'le', prospectProfile.gpa.value.upper)
    );
  }

  dispatch(
    setGPAQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setTestScoresQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ProspectProfileReducerState} prospectProfile - The current state
 * of the Prospect Profile filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setTestScoresQueryValue(dispatch, prospectProfile)
 */
export const setTestScoresQueryValue = (
  dispatch: Dispatch<any>,
  prospectProfile: ProspectProfileReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (prospectProfile.testScores.selection) {
    case 'SAT':
      filterObjectArray.push('EITHER');
      filterObjectArray.push(
        constructFilterObject(
          'sat',
          'ge',
          prospectProfile.testScores.value.sat.lower
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'sat',
          'le',
          prospectProfile.testScores.value.sat.upper
        )
      );
      break;
    case 'ACT':
      filterObjectArray.push('EITHER');
      filterObjectArray.push(
        constructFilterObject(
          'act',
          'ge',
          prospectProfile.testScores.value.act.lower
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'act',
          'le',
          prospectProfile.testScores.value.act.upper
        )
      );
      break;
    case 'SAT or ACT':
    case 'SAT and ACT':
      filterObjectArray.push(
        prospectProfile.testScores.selection.split(' ')[1].toUpperCase()
      );
      filterObjectArray.push(
        constructFilterObject(
          'sat',
          'ge',
          prospectProfile.testScores.value.sat.lower
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'sat',
          'le',
          prospectProfile.testScores.value.sat.upper
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'act',
          'ge',
          prospectProfile.testScores.value.act.lower
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'act',
          'le',
          prospectProfile.testScores.value.act.upper
        )
      );
      break;
    default:
      break;
  }

  dispatch(
    setTestScoresQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setDateRegisteredQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setDateRegisteredQueryValue(dispatch, prospectPengagement)
 */
export const setDateRegisteredQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.dateRegistered.selection) {
    case 'Select Recency':
      filterObjectArray.push('Select Recency');
      filterObjectArray.push(
        constructFilterObject(
          'dateRegistered',
          'ge',
          findDateFromDays(
            stringToDaysLookup(engagement.dateRegistered.value.label)
          )
        )
      );
      break;
    case 'Specific Date Range':
      filterObjectArray.push('Specific Date Range');
      filterObjectArray.push(
        constructFilterObject(
          'dateRegistered',
          'ge',
          calendarDateTranslate(engagement.dateRegistered.value.lower)
        )
      );
      filterObjectArray.push(
        constructFilterObject(
          'dateRegistered',
          'le',
          calendarDateTranslate(engagement.dateRegistered.value.upper)
        )
      );
      break;
    default:
      break;
  }

  dispatch(
    setDateRegisteredQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setRelativeEngagementQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setRelativeEngagementQueryValue(dispatch, prospengagement)
 */
export const setRelativeEngagementQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray = [
    (engagement.relativeEngagement.high &&
      constructFilterObject('relativeInterestLevel', 'eq', 'high')) ||
      undefined,
    (engagement.relativeEngagement.medium &&
      constructFilterObject('relativeInterestLevel', 'eq', 'medium')) ||
      undefined,
    (engagement.relativeEngagement.low &&
      constructFilterObject('relativeInterestLevel', 'eq', 'low')) ||
      undefined,
  ].filter((filterObject) => typeof filterObject !== 'undefined');

  dispatch(
    setRelativeEngagementQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setTotalTimeSpentQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setTotalTimeSpentQueryValue(dispatch, prospectPengagement)
 */
export const setTotalTimeSpentQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.totalTimeSpent.selection) {
    case 'VT':
      filterObjectArray.push('EITHER');
      filterObjectArray.push(
        constructFilterObject(
          'totalVirtualTourSessionTimeMinutesAllTime',
          'ge',
          String(engagement.totalTimeSpent.value.vt.lower)
        )
      );
      if (engagement.totalTimeSpent.value.vt.upper !== 15) {
        filterObjectArray.push(
          constructFilterObject(
            'totalVirtualTourSessionTimeMinutesAllTime',
            'le',
            String(engagement.totalTimeSpent.value.vt.upper)
          )
        );
      }
      break;
    case 'IWC':
      filterObjectArray.push('EITHER');
      filterObjectArray.push(
        constructFilterObject(
          'totalIWCSessionTimeMinutesAllTime',
          'ge',
          String(engagement.totalTimeSpent.value.iwc.lower)
        )
      );
      if (engagement.totalTimeSpent.value.iwc.upper !== 15) {
        filterObjectArray.push(
          constructFilterObject(
            'totalIWCSessionTimeMinutesAllTime',
            'le',
            String(engagement.totalTimeSpent.value.iwc.upper)
          )
        );
      }
      break;
    case 'VT or IWC':
    case 'VT and IWC':
      filterObjectArray.push(
        engagement.totalTimeSpent.selection.split(' ')[1].toUpperCase()
      );
      filterObjectArray.push(
        constructFilterObject(
          'totalVirtualTourSessionTimeMinutesAllTime',
          'ge',
          String(engagement.totalTimeSpent.value.vt.lower)
        )
      );
      if (engagement.totalTimeSpent.value.vt.upper !== 15) {
        filterObjectArray.push(
          constructFilterObject(
            'totalVirtualTourSessionTimeMinutesAllTime',
            'le',
            String(engagement.totalTimeSpent.value.vt.upper)
          )
        );
      } else {
        filterObjectArray.push(undefined);
      }
      filterObjectArray.push(
        constructFilterObject(
          'totalIWCSessionTimeMinutesAllTime',
          'ge',
          String(engagement.totalTimeSpent.value.iwc.lower)
        )
      );
      if (engagement.totalTimeSpent.value.iwc.upper !== 15) {
        filterObjectArray.push(
          constructFilterObject(
            'totalIWCSessionTimeMinutesAllTime',
            'le',
            String(engagement.totalTimeSpent.value.iwc.upper)
          )
        );
      } else {
        filterObjectArray.push(undefined);
      }
      break;
    default:
      break;
  }

  dispatch(
    setTotalTimeSpentQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setScheduledVisitQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setScheduledVisitQueryValue(dispatch, prospectPengagement)
 */
export const setScheduledVisitQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.scheduledVisit) {
    case 'Yes':
      filterObjectArray.push(
        constructFilterObject('hasScheduledVisitAllTime', 'eq', 'TRUE')
      );
      break;
    case 'No':
      filterObjectArray.push(
        constructFilterObject('hasScheduledVisitAllTime', 'eq', 'FALSE')
      );
      break;
    default:
      break;
  }

  dispatch(
    setScheduledVisitQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setAppliedQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setAppliedQueryValue(dispatch, prospectProfile)engagement)
 */
export const setAppliedQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.applied) {
    case 'Yes':
      filterObjectArray.push(
        constructFilterObject('hasAppliedAllTime', 'eq', 'TRUE')
      );
      break;
    case 'No':
      filterObjectArray.push(
        constructFilterObject('hasAppliedAllTime', 'eq', 'FALSE')
      );
      break;
    default:
      break;
  }

  dispatch(
    setAppliedQuery((filterObjectArray.length > 0 && filterObjectArray) || '')
  );
};

/**
 * @function setActivityWithInstitutionQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setActivityWithInstitutionQueryValue(dispatch, engagement)
 */
export const setActivityWithInstitutionQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.activityWithInstitution.selection) {
    case 'Active':
      filterObjectArray.push(
        constructFilterObject(
          'lastDateActiveWithInstitution',
          'ge',
          findDateFromDays(
            stringToDaysLookup(engagement.activityWithInstitution.value.label)
          )
        )
      );
      break;
    case 'Inactive':
      filterObjectArray.push(
        constructFilterObject(
          'lastDateActiveWithInstitution',
          'le',
          findDateFromDays(
            stringToDaysLookup(engagement.activityWithInstitution.value.label)
          )
        )
      );
      break;
    default:
      break;
  }

  dispatch(
    setActivityWithInstitutionQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setActivityWithEABQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {EngagementReducerState} engagement - The current state
 * of the Engagement filter section.
 * @return {FilterObject[] | ""} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setActivityWithEABQueryValue(dispatch, prospectengagement)
 */
export const setActivityWithEABQueryValue = (
  dispatch: Dispatch<any>,
  engagement: EngagementReducerState
) => {
  const filterObjectArray: any[] = [];

  switch (engagement.activityWithEAB.selection) {
    case 'Active':
      filterObjectArray.push(
        constructFilterObject(
          'lastDateActiveWithYouvisit',
          'ge',
          findDateFromDays(
            stringToDaysLookup(engagement.activityWithEAB.value.label)
          )
        )
      );
      break;
    case 'Inactive':
      filterObjectArray.push(
        constructFilterObject(
          'lastDateActiveWithYouvisit',
          'le',
          findDateFromDays(
            stringToDaysLookup(engagement.activityWithEAB.value.label)
          )
        )
      );
      break;
    default:
      break;
  }

  dispatch(
    setActivityWithEABQuery(
      (filterObjectArray.length > 0 && filterObjectArray) || ''
    )
  );
};

/**
 * @function setListMembershipQueryValue
 *
 * Builds a piece of the needed query from the state passed in.
 * Meant to be used in conjuction with the function getBuiltQuery
 * found in queries.utilities.ts in order to get the completed
 * object readable by the back-end API.
 *
 * @param {Dispatch} dispatch - Dispatch
 * @param {ListMembershipReducerState} listMembership - The current state
 * of the Engagement filter section.
 * @return {string} - Doesn't return in the conventional
 * sense but instead dispatched an action using the function of a
 * similar name to the action-creator that will propogate this information
 * to the Redux store.
 *
 * @example
 *
 *     setListMembershipQueryValue(dispatch, listMembership)
 */
export const setListMembershipQueryValue = (
  dispatch: Dispatch<any>,
  listMembership: ListMembershipReducerState
) => {
  const listIDArray = listMembership.lists.map((list) => {
    return list.value;
  });

  dispatch(
    setListMembershipQuery(
      (listIDArray.length > 0 && listIDArray.join(',')) || ''
    )
  );
};

export const setLocationsQueryValue = (
  dispatch: Dispatch<any>,
  locations: LocationsReducerState
) => {
  dispatch(
    setLocationsQuery(
      (locations.locationIds.length > 0 && locations.locationIds.join(',')) ||
        ''
    )
  );
};

export const setQueryIsIncongruentValue = (
  dispatch: Dispatch<any>,
  value: boolean
) => {
  dispatch(setQueryIsIncongruent(value));
};
